import axios from 'axios';
import { isInChinaSync } from '../utils/regionDetect';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  // Global request timeout. Without this, a stalled connection (common on the
  // China ↔ US link / GFW interference) hangs forever — the caller's finally{}
  // never runs and pages like /profiles get stuck in a permanent loading
  // spinner. 180s caps it so the request rejects, loading resets, and the user
  // can retry. Uploads override this with timeout:0 (see below).
  timeout: 180000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Tell the backend which region the user is in so AI-routed endpoints
    // (bio gen, KPI v2 ai-summary, /confirmation/ai-generate, etc.) can
    // pick Kimi for CN users and OpenAI for everyone else. The header is
    // a hint — backend still has its own server-side probe as fallback.
    if (isInChinaSync()) {
      config.headers['X-User-Region'] = 'cn';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 操作引导 demo mode (components/tour). While a guided tour runs, the TourProvider installs a
// handler that answers GETs for the sample member / team from fixtures and refuses every write;
// the request never leaves the browser. handler(config) → undefined (send normally) |
// { data } (fixture) | { block: true, message } (rejected like a 4xx, response.data.error = message).
let demoHandler = null;
export const setDemoHandler = (fn) => { demoHandler = typeof fn === 'function' ? fn : null; };
api.interceptors.request.use((config) => {
  if (!demoHandler) return config;
  let out;
  try { out = demoHandler(config); } catch (e) { out = undefined; }
  if (!out) return config;
  config.adapter = () => {
    if (out.block) {
      const err = new Error(out.message || 'demo mode');
      err.isDemoBlocked = true;
      err.config = config;
      err.response = { status: 423, statusText: 'Demo', headers: {}, config, data: { error: out.message || '示例模式：不会保存', demo: true } };
      return Promise.reject(err);
    }
    return Promise.resolve({ data: out.data, status: 200, statusText: 'OK', headers: {}, config, request: {} });
  };
  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Session expiry: clear the stale token and signal the app to show an
    // in-place re-login modal (instead of redirecting to /login and losing the
    // user's place). The modal is mounted at the app root and listens for the
    // `session-expired` event; `user` stays in memory so the page underneath is
    // not unmounted.
    if (error.response?.status === 401) {
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      if (!isAuthEndpoint) {
        const hadToken = !!localStorage.getItem('token');
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        if (hadToken && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('session-expired'));
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getProfile: () => api.get('/auth/profile'),
  getSettings: () => api.get('/auth/settings'),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateSettings: (data) => api.put('/auth/settings', data),
  changePassword: (data) => api.put('/auth/password', data),
  updateNotifications: (data) => api.put('/auth/notifications', data),
  updatePreferences: (data) => api.put('/auth/preferences', data),
  updateCompanySettings: (data) => {
    // Let axios handle FormData headers automatically
    const config = {};
    if (data instanceof FormData) {
      config.headers = { 'Content-Type': 'multipart/form-data' };
    }
    return api.put('/auth/company-settings', data, config);
  },
};

// Profiles API
export const profilesAPI = {
  getAll: (params) => api.get('/profiles', { params }),
  // Client e-signature links (DocuSign-style).
  createSignatureRequest: (id) => api.post(`/profiles/${id}/signature-request`),
  getSignatureRequest: (id) => api.get(`/profiles/${id}/signature-request`),
  getPublicSign: (token) => api.get(`/public/sign/${token}`),
  submitPublicSign: (token, data) => api.post(`/public/sign/${token}`, data),
  // Generate / reset a client portal login for a profile. Returns
  // { email, password, login_url } so the caller can show / copy them.
  generateLogin: (id) => api.post(`/profiles/${id}/generate-login`),
  getMyClientStats: () => api.get('/profiles/my-client-stats'),
  setCoachSortOrder: (id, value) => api.put(`/profiles/${id}/coach-sort-order`, { coach_sort_order: value }),
  getById: (id) => api.get(`/profiles/${id}`),
  // Per-profile follow-up history (kpi_v2 ticket-events of type 'followup'
  // across all tickets whose client_profile_id matches this profile).
  getFollowups: (id) => api.get(`/profiles/${id}/followups`),
  create: (data) => api.post('/profiles', data),
  update: (id, data) => api.put(`/profiles/${id}`, data),
  delete: (id) => api.delete(`/profiles/${id}`),
  patchStar:    (id, star) => api.patch(`/profiles/${id}/star`, { star_rating: star || null }),
  patchStaffNotes: (id, notes) => api.patch(`/profiles/${id}/staff-notes`, { staff_notes: notes ?? null }),
  // Quick privacy toggles (资料公开/隐藏 = is_public, 照片公开/隐藏 = show_photos).
  patchVisibility: (id, data) => api.patch(`/profiles/${id}/visibility`, data),
  patchCanChat: (id, can_chat) => api.patch(`/profiles/${id}/can-chat`, { can_chat: can_chat ? 1 : 0 }),
  // 客户管道看板 drag-to-move. Deliberately not PUT /profiles/:id — that route
  // parks matchmaker edits in the admin review queue, so a drag would silently
  // do nothing. See PATCH /profiles/:id/pipeline-stage.
  patchPipelineStage: (id, stage) => api.patch(`/profiles/${id}/pipeline-stage`, { pipeline_stage: stage }),
  getActivity: (id) => api.get(`/profiles/${id}/activity`),
  // params: { from, to } (YYYY-MM-DD) for the 今日待办 calendar; omitted keeps
  //         the endpoint's original next-48h / past-14d windows.
  getTodayQueue: (params) => api.get('/me/today-queue', { params }),
  // The coach assistant's chat turn — answers over the caller's own queue.
  assistantChat: (data) => api.post('/me/assistant/chat', data),
  // 紧急客户 — manual urgent flag: GET → { flag|null, can_edit }; set { reason, due_date? }; clear.
  getUrgentFlag: (id) => api.get(`/profiles/${id}/urgent-flag`),
  setUrgentFlag: (id, data) => api.post(`/profiles/${id}/urgent-flag`, data),
  clearUrgentFlag: (id) => api.post(`/profiles/${id}/urgent-flag/clear`),
  getComplaints: (id) => api.get(`/profiles/${id}/complaints`),
  createComplaint: (id, data) => api.post(`/profiles/${id}/complaints`, data),
  // data: { level?, status?, assignee_user_id?, result? } — result required when status → resolved.
  updateComplaint: (id, complaintId, data) => api.put(`/profiles/${id}/complaints/${complaintId}`, data),
  deleteComplaint: (id, complaintId) => api.delete(`/profiles/${id}/complaints/${complaintId}`),
  // params: { author } — 记录人 filter. Items carry the 待办 fields
  // (remind_at / assignee / done_* / images / state); the response also has
  // todo_counts { open, overdue } and authors [{ id, name }].
  getProfileNotes: (id, params) => api.get(`/profiles/${id}/notes`, { params }),
  // data: { note, remind_at?, assignee_user_id?, images? } — remind_at makes it a 待办.
  createProfileNote: (id, data) => api.post(`/profiles/${id}/notes`, data),
  markNoteDone: (id, noteId, result) => api.put(`/profiles/${id}/notes/${noteId}/done`, { result: result || null }),
  reopenNote: (id, noteId) => api.put(`/profiles/${id}/notes/${noteId}/reopen`),
  // Author-only edit / delete, used by the 客户详情 timeline.
  updateProfileNote: (id, noteId, note) => api.put(`/profiles/${id}/notes/${noteId}`, { note }),
  deleteProfileNote: (id, noteId) => api.delete(`/profiles/${id}/notes/${noteId}`),
  listExtensions: (id) => api.get(`/profiles/${id}/extensions`),
  addExtension:   (id, data) => api.post(`/profiles/${id}/extensions`, data),
  getPublic: (slug) => api.get(`/public/profile/${slug}`),
  getTypes: () => api.get('/profiles/types'),
  getGlobalCustomFields: () => api.get('/profiles/global-custom-fields'),
  getCustomFields: () => api.get('/profiles/custom-fields'),
  createCustomField: (data) => api.post('/profiles/custom-fields', data),
  updateCustomField: (id, data) => api.put(`/profiles/custom-fields/${id}`, data),
  deleteCustomField: (id) => api.delete(`/profiles/custom-fields/${id}`),
  markStagePaid: (profileId, stageId) => api.post(`/profiles/${profileId}/mark-stage-paid`, { stage_id: stageId }),
};

// Daily check-in / coach-lifecycle panel API
export const coachDailyContactsAPI = {
  today: () => api.get('/coach/daily-contacts/today'),
  log: (profile_id, kind, notes, images, next_follow_up_at = null) =>
    api.post('/coach/daily-contacts', { profile_id, kind, notes, images, next_follow_up_at }),
  patch: (profile_id, data) => api.patch(`/coach/daily-contacts/${profile_id}`, data),
  remove: (profile_id) => api.delete(`/coach/daily-contacts/${profile_id}`),
  // By-entry variants — the pair above only reaches TODAY's row for the calling
  // coach, so the timeline (which shows older check-ins) addresses rows by id.
  patchEntry: (entryId, data) => api.patch(`/coach/daily-contacts/entry/${entryId}`, data),
  removeEntry: (entryId) => api.delete(`/coach/daily-contacts/entry/${entryId}`),
  timeline: (profile_id) => api.get(`/coach/daily-contacts/timeline/${profile_id}`),
  // Coach: submit today's entry for supervisor/admin review.
  submit: (profile_id) => api.post(`/coach/daily-contacts/${profile_id}/submit`),
  // Bulk reorder profiles.coach_sort_order (1..N in array order).
  reorderProfiles: (order) => api.put('/profiles/coach-sort-order/bulk', { order }),
  // Supervisor / admin: approve or reject a specific entry by its row id.
  approve: (id, notes) => api.put(`/admin/daily-contacts/${id}/approve`, { notes }),
  reject:  (id, notes) => api.put(`/admin/daily-contacts/${id}/reject`,  { notes }),
  // Read-only listing for supervisors / admins.
  listAll: (params) => api.get('/admin/daily-contacts', { params }),
  // Upload one image; returns { url, filename }.
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  },
};

// ── VIP 服务流程 5.0 (phase 1) ─────────────────────────────────────────────
// Service status line, contact frequency, meeting checks and the 5.0 打卡.
export const serviceSopAPI = {
  // → { service_start_at, service_day, in_focus_period, interval_hours, pause_until,
  //     pending_interval_request, last_contact_at, next_due_at, overdue_hours,
  //     todos: { open, overdue }, complaints: { open, urgent_open }, report, can_approve }
  status: (profileId) => api.get(`/profiles/${profileId}/service-status`),
  // ids: number[] (≤300) → { statuses: { [id]: { service_day, in_focus_period, interval_hours, next_due_at,
  //   overdue_hours, next_follow_up_at, todos: { open, overdue }, todo_due_at } } }
  statusBulk: (ids) => api.get('/service-status/bulk', { params: { ids: ids.join(',') } }),
  // → { meetings: [{ dating_schedule_id, partner_profile_id, partner_label, scheduled_at, status, ended,
  //                  checks: { pre_self, pre_partner, post_self } }] }
  meetingChecks: (profileId) => api.get(`/profiles/${profileId}/meeting-checks`),
  // data: { interval_hours?: 48|72|168, pause_until?: 'YYYY-MM-DD', reason }
  requestInterval: (profileId, data) => api.post(`/profiles/${profileId}/contact-interval-requests`, data),
  // data: { action: 'approve'|'reject', note? } — 主管 roles only.
  reviewInterval: (requestId, data) => api.put(`/contact-interval-requests/${requestId}`, data),
  // The 5.0 打卡: { profile_id, kind, notes, images, next_follow_up_at, purpose, method, outcome,
  //                 dating_schedule_id, contacted_at }
  logContact: (data) => api.post('/coach/daily-contacts', data),
};

// ── 服务进度 双方截图 + 安排第 N 次约会 (2026-10). May 404 before the backend
// deploy — callers hide the widget then.
export const stepExtrasAPI = {
  // → { items: [{ id, match_id, step_key, party: 'self'|'other', url, uploaded_by, uploaded_by_name, created_at }] }
  attachments: (profileId) => api.get(`/profiles/${profileId}/step-attachments`),
  // data: { match_id, step_key, party, urls } → { items }
  addAttachments: (profileId, data) => api.post(`/profiles/${profileId}/step-attachments`, data),
  removeAttachment: (id) => api.delete(`/step-attachments/${id}`),
  // → { partners: [{ partner_id, name, offline_id, match_id, match_status, dates, past_dates, next_round, last, upcoming }] }
  datedPartners: (profileId) => api.get(`/profiles/${profileId}/dated-partners`),
  // data: { partner_id, scheduled_date, scheduled_time (Pacific), location?, note? } → { schedule, notified }
  createNextDate: (profileId, data) => api.post(`/profiles/${profileId}/next-date`, data),
};

// ── 扫码上传 (2026-10): one-time QR upload links for 客户记录 image slots. May 404 before the
// backend deploy — the 「扫码上传」 buttons stay hidden then (components/profiles/workspace/QrUpload.js).
export const uploadTokensAPI = {
  // → { enabled, ttl_minutes, max_per_request, max_file_mb, max_per_token }
  config: () => api.get('/upload-tokens/config'),
  // target: { kind:'contact' } | { kind:'step', match_id, step_key, party } | { kind:'step_note', step_key }
  //         | { kind:'note' } | { kind:'complaint' }  → { id, token, url: '/u/<token>', expires_at, label, target }
  create: (profileId, target) => api.post(`/profiles/${profileId}/upload-tokens`, { target }),
  // → { id, state: 'live'|'expired'|'revoked', expires_at, files: [{ id, url, attachment_id, created_at }], count }
  status: (id) => api.get(`/upload-tokens/${id}`),
  revoke: (id) => api.delete(`/upload-tokens/${id}`),
  // Public (no login) — the phone page.
  publicMeta: (token) => api.get(`/public/upload/${encodeURIComponent(token)}`),
  publicUpload: (token, files, onUploadProgress) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f, f.name || 'photo.jpg'));
    return api.post(`/public/upload/${encodeURIComponent(token)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0, onUploadProgress });
  },
};

// ── 客户工作台 (VIP 服务流程 5.0, round 2) ─────────────────────────────────
// Every call here may 404 on an API that has not been deployed yet; callers
// treat that as "not available" and fall back to the phase-1 data.
export const workspaceAPI = {
  // Service-team colleagues (核实老师 / 整改负责老师 pickers) → { staff: [{ id, first_name, last_name, role }] }
  staff: () => api.get('/service/staff'),
  // → { profile, service_day, stage_name, light, light_reasons, status, steps: [Step], round,
  //     next_actions: [{ key, title, why, due_at, overdue, severity, action, action_args }],
  //     todos: [...], reviews_pending: { contacts, interval_requests, reports, rectifications } }
  // params: { round? } — an earlier round of the 推荐/约见 steps.
  get: (profileId, params) => api.get(`/profiles/${profileId}/workspace`, { params }),
  // 服务进度 read-only, for members who are not the viewer's own (403 on /workspace)
  progress: (profileId, params) => api.get(`/profiles/${profileId}/sop-progress`, { params }),
  catalogue: () => api.get('/service/sop-catalogue'),
  // data: { round?, note?, images?, verified_by? }
  tickStep: (profileId, key, data) => api.post(`/profiles/${profileId}/sop-steps/${key}`, data || {}),
  untickStep: (profileId, key, round) => api.delete(`/profiles/${profileId}/sop-steps/${key}`, { params: round ? { round } : {} }),
  // 管理员 only: override 'done' | 'undone' | null (恢复自动判断) — body { override, note?, images?, round?, choice? }
  overrideStep: (profileId, key, data) => api.post(`/profiles/${profileId}/sop-steps/${key}`, data || {}),
  editStepNote: (profileId, key, note, round) => api.put(`/profiles/${profileId}/sop-steps/${key}/note`, { note, round }),
  // → { fields, updated_at, updated_by_name, history: [{ changes, reason, summary, changed_by_name, created_at }] }
  getQuickCard: (profileId) => api.get(`/profiles/${profileId}/quick-card`),
  suggestQuickCard: (profileId) => api.get(`/profiles/${profileId}/quick-card/suggest`),
  // data: { fields, reason }
  saveQuickCard: (profileId, data) => api.put(`/profiles/${profileId}/quick-card`, data),
  // 主管: → { items: [{ profile_id, name, offline_id, coach_name, contacts_submitted, interval_requests,
  //                     reports_submitted, rectifications_open, light }], counts }
  reviewQueue: () => api.get('/service/review-queue'),
  // data: { decision: 'approved'|'rejected', note }
  reviewContact: (contactId, data) => api.put(`/service/contacts/${contactId}/review`, data),
  // → { items: [{ key, label, ok, detail }] }
  kpiCheck: (profileId) => api.get(`/profiles/${profileId}/kpi-check`),
  // Not in the written contract: the list of a member's 整改单. Falls back to
  // workspace.rectifications when absent.
  listRectifications: (profileId) => api.get(`/profiles/${profileId}/rectifications`),
  // data: { title, due_at, coach_user_id? }
  createRectification: (profileId, data) => api.post(`/profiles/${profileId}/rectifications`, data),
  // data: { action: 'done'|'verify', note }
  updateRectification: (id, data) => api.put(`/rectifications/${id}`, data),
  // 会员心动 → { current: [{ target: { id, first_name, last_name, offline_id, photo, gender, age, city }, favorited_at,
  //   mutual, recommended, dated }], events: [{ action: 'add'|'remove', at, target }], counts: { current, mutual } }
  memberFavorites: (profileId) => api.get(`/profiles/${profileId}/member-favorites`),
};

// 操作引导 (guided tour) progress. May 404 before the backend deploy — the tour then keeps
// progress in localStorage (components/tour/TourProvider.js).
export const tourAPI = {
  // → { version, routes: { coach: { last_step, total, completed, dismissed, outdated, weekly_reminder }, supervisor }, default_route }
  get: () => api.get('/service/tour'),
  // data: { last_step?, completed?, dismissed?, weekly_reminder? }
  save: (route, data) => api.put(`/service/tour/${route}`, data),
  // 主管: bell to that user linking to /guide?route=…; 409 when already reminded today.
  remind: (userId, route = 'coach') => api.post('/service/tour/remind', { user_id: userId, route }),
};

// 月度服务报告 (staff side).
// ── 服务监督 (VIP 服务流程 5.0, round 3) ───────────────────────────────────
// 主管 roles see every coach; a plain coach sees only themself. May 404 until
// the backend is deployed — the page shows "部署后可用".
export const supervisionAPI = {
  // params: { from?, to? } (YYYY-MM-DD) → { period, team: { members, red, yellow, green, pending_reviews,
  //   rectifications_open, kpi }, coaches: [{ id, name, photo, members, red, yellow, green, pending_reviews,
  //   rectifications_open, rectifications_overdue, contacts_two_way, contacts_no_reply, kpi, score, flags }] }
  summary: (params) => api.get('/service/supervision', { params }),
  // → { coach, kpi, score, flags, summary, members: [{ profile_id, name, offline_id, photo, service_day, light,
  //   light_reasons, stage_name, last_contact_at, overdue_hours, todos_open, todos_overdue, pending_reviews, rectifications_open }] }
  coach: (coachId, params) => api.get(`/service/supervision/coach/${coachId}`, { params }),
  // params: { coach_id?, profile_id?, type?: contact|note|rectification|step|quickcard|complaint|all, review_status?,
  //   from?, to?, q?, page?, page_size? } → { items: [...], total, page, page_size, by_type }
  records: (params) => api.get('/service/records', { params }),
};

// Per-user UI preferences (e.g. grid column order) — always the logged-in user's own.
// GET → { key, value, updated_at } (value null when nothing saved). Old backend → 404.
export const userPreferencesAPI = {
  get: (key) => api.get(`/user-preferences/${encodeURIComponent(key)}`),
  put: (key, value) => api.put(`/user-preferences/${encodeURIComponent(key)}`, { value }),
  remove: (key) => api.delete(`/user-preferences/${encodeURIComponent(key)}`),
};

// 会员评价 (members' ratings of their 售后红娘). mine → { average, count, average_90d, count_90d, ratings };
// summary (主管 roles) → { days, average, count, coaches: [{ coach_id, coach_name, average, count, low_count, last_at }], recent }.
export const coachRatingsAPI = {
  mine: (params) => api.get('/coach-ratings/mine', { params }),
  summary: (params) => api.get('/coach-ratings/summary', { params }),
};

// 总推荐状态 of one member → { rec_status, rec_count, rec_matches }.
export const recStatusAPI = {
  get: (profileId) => api.get(`/profiles/${profileId}/rec-status`),
};

export const serviceReportsAPI = {
  list: (params) => api.get('/service-reports', { params }),            // { status, mine }
  get: (id) => api.get(`/service-reports/${id}`),
  save: (id, data) => api.put(`/service-reports/${id}`, data),           // { opening, timeline, learned, plan, internal_note }
  submit: (id) => api.post(`/service-reports/${id}/submit`),
  review: (id, data) => api.post(`/service-reports/${id}/review`, data), // { action, note, checklist }
  aiDraft: (id) => api.post(`/service-reports/${id}/ai-draft`, {}, { timeout: 0 }),
  forProfile: (profileId) => api.get(`/profiles/${profileId}/service-reports`),
};

// ── KPIv2 (after-sales) ──────────────────────────────────────────────────
export const kpiV2API = {
  listTickets: (params) => api.get('/kpi-v2/tickets', { params }),
  getTicket: (id) => api.get(`/kpi-v2/tickets/${id}`),
  createTicket: (data) => api.post('/kpi-v2/tickets', data),
  updateTicket: (id, data) => api.patch(`/kpi-v2/tickets/${id}`, data),
  reorderTickets: (order) => api.post('/kpi-v2/tickets/reorder', { order }),
  deleteTicket: (id) => api.delete(`/kpi-v2/tickets/${id}`),
  // Two-stage status-change workflow (coach submit → leader_coach approves →
  // supervisor approves → status commits). Admins bypass via updateTicket.
  submitStatusReview: (id, to_status) => api.post(`/kpi-v2/tickets/${id}/submit-status-review`, { to_status }),
  reviewStatus: (id, stage, status, notes) => api.post(`/kpi-v2/tickets/${id}/review-status`, { stage, status, notes }),
  // Leader/supervisor/admin review dashboard — every ticket with a pending
  // stage submission. Powers the 概览 (Overview) tab.
  pendingStageReviews: () => api.get('/kpi-v2/pending-stage-reviews'),
  // Stage fines (kpi_v2_stage_fines). Used by the 罚款 (Fines) tab.
  listStageFines: (params) => api.get('/kpi-v2/stage-fines', { params }),
  createStageFine: (data) => api.post('/kpi-v2/stage-fines', data),
  deleteStageFine: (id) => api.delete(`/kpi-v2/stage-fines/${id}`),
  updateStageFine: (id, data) => api.patch(`/kpi-v2/stage-fines/${id}`, data),
  appendEvent: (id, data) => api.post(`/kpi-v2/tickets/${id}/events`, data),
  getClientMessages: (id) => api.get(`/kpi-v2/tickets/${id}/client-messages`),
  sendClientMessage: (id, content) => api.post(`/kpi-v2/tickets/${id}/client-message`, { content }),
  getQuestionnaireSubmission: (submissionId) => api.get(`/kpi-v2/questionnaire-submissions/${submissionId}`),
  // Profile-scoped variants — readable by anyone who may view the profile,
  // where the ticket-scoped route above 403s outside the sending ticket.
  listProfileQuestionnaires: (profileId) => api.get(`/profiles/${profileId}/questionnaire-submissions`),
  getProfileQuestionnaire: (profileId, submissionId) =>
    api.get(`/profiles/${profileId}/questionnaire-submissions/${submissionId}`),
  getMeeting: (meetingId) => api.get(`/kpi-v2/meetings/${meetingId}`),
  probeMeetingRecording: (meetingId) => api.post(`/kpi-v2/meetings/${meetingId}/probe-recording`),
  getMatchReview: (reviewId) => api.get(`/kpi-v2/match-reviews/${reviewId}`),
  createPartnerPortraitReview: (ticket_id) => api.post('/kpi-v2/partner-portrait-reviews', { ticket_id }),
  getPublicPartnerPortrait: (token) => api.get(`/public/partner-portrait-review/${token}`),
  respondPartnerPortrait: (token, action, note) => api.post(`/public/partner-portrait-review/${token}/respond`, { action, note }),
  deleteEvent: (ticketId, eventId) => api.delete(`/kpi-v2/tickets/${ticketId}/events/${eventId}`),
  reviewEvent: (ticketId, eventId, data) => api.post(`/kpi-v2/tickets/${ticketId}/events/${eventId}/review`, data),
  stats: () => api.get('/kpi-v2/tickets/stats'),
  seedDemo: () => api.post('/kpi-v2/seed-demo'),
  // Resolve a client by their offline_id (profile custom field 115).
  lookupClient: (offline_id) => api.get('/kpi-v2/lookup-client', { params: { offline_id } }),
  // Partial-match search for the ticket-create client picker.
  searchClients: (q, limit, gender) => api.get('/kpi-v2/search-clients', { params: { q, limit, gender } }),
  // Follow-ups (T+N)
  listFollowups: (params) => api.get('/kpi-v2/followups', { params }),
  followupStats: () => api.get('/kpi-v2/followups/stats'),
  createFollowup: (data) => api.post('/kpi-v2/followups', data),
  updateFollowup: (id, data) => api.patch(`/kpi-v2/followups/${id}`, data),
  deleteFollowup: (id) => api.delete(`/kpi-v2/followups/${id}`),
  // AI Copilot (OpenAI-backed). Pass `{}` (not null) so body-parser's
  // strict-JSON mode accepts the request.
  aiSummary: (id, lang) => api.post(`/kpi-v2/tickets/${id}/ai-summary`, {}, { params: lang ? { lang } : undefined }),
  aiDraftReply: (id, tone, lang) =>
    api.post(`/kpi-v2/tickets/${id}/ai-draft-reply`, { tone }, { params: lang ? { lang } : undefined }),
  // Dashboard widgets
  trend24h: () => api.get('/kpi-v2/dashboard/trend24h'),
  activity: (limit) => api.get('/kpi-v2/dashboard/activity', { params: limit ? { limit } : undefined }),
  // Knowledge base
  listKnowledge: (params) => api.get('/kpi-v2/knowledge', { params }),
  getKnowledge: (id) => api.get(`/kpi-v2/knowledge/${id}`),
  createKnowledge: (data) => api.post('/kpi-v2/knowledge', data),
  updateKnowledge: (id, data) => api.patch(`/kpi-v2/knowledge/${id}`, data),
  deleteKnowledge: (id) => api.delete(`/kpi-v2/knowledge/${id}`),
  // Knowledge base categories (admin only — CRUD)
  listKnowledgeCategories: () => api.get('/kpi-v2/knowledge-categories'),
  createKnowledgeCategory: (data) => api.post('/kpi-v2/knowledge-categories', data),
  updateKnowledgeCategory: (id, data) => api.patch(`/kpi-v2/knowledge-categories/${id}`, data),
  deleteKnowledgeCategory: (id) => api.delete(`/kpi-v2/knowledge-categories/${id}`),
  // RAG suggest for a ticket. Empty body (not null) for body-parser strict.
  kbSuggest: (ticketId, lang) =>
    api.post(`/kpi-v2/tickets/${ticketId}/kb-suggest`, {}, { params: lang ? { lang } : undefined }),
  // Staff (admin-only)
  listStaff: (params) => api.get('/kpi-v2/staff', { params }),
  updateStaff: (userId, data) => api.patch(`/kpi-v2/staff/${userId}`, data),
  staffMap: () => api.get('/kpi-v2/staff/map'),
  // Client candidates — saved opposite-sex profiles for future matching.
  listCandidates: (profileId) => api.get(`/kpi-v2/clients/${profileId}/candidates`),
  addCandidate: (profileId, data) => api.post(`/kpi-v2/clients/${profileId}/candidates`, data),
  removeCandidate: (candidateRowId) => api.delete(`/kpi-v2/candidates/${candidateRowId}`),
  // Generic upload (returns { url, filename }) — used for ticket attachments.
  uploadFile: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  },
  // Settings (admin-only)
  listSettings: () => api.get('/kpi-v2/settings'),
  updateSettings: (data) => api.put('/kpi-v2/settings', data),
  // Public read-only kanban_columns (open to every KPIv2 role so non-admin
  // coaches see the same admin-configured columns).
  getKanbanColumns: () => api.get('/kpi-v2/kanban-columns'),
  // Public read-only ticket status catalog (admin-managed).
  getTicketStatuses: () => api.get('/kpi-v2/ticket-statuses'),
  // Public read-only ticket type catalog (admin-managed).
  getTicketTypes: () => api.get('/kpi-v2/ticket-types'),
};

// Directory API
export const directoryAPI = {
  getDirectory: (params) => api.get('/directory', { params }),
  getProfileById: (id) => api.get(`/directory/profile/${id}`),
  getFavorites: () => api.get('/directory/favorites'),
  addFavorite: (profileId) => api.post('/directory/favorites', { profile_id: profileId }),
  removeFavorite: (profileId) => api.delete(`/directory/favorites/${profileId}`),
  getCollections: () => api.get('/directory/collections'),
  createCollection: (data) => api.post('/directory/collections', data),
  updateCollection: (id, data) => api.put(`/directory/collections/${id}`, data),
  deleteCollection: (id) => api.delete(`/directory/collections/${id}`),
  getCollectionProfiles: (id) => api.get(`/directory/collections/${id}/profiles`),
  shareCollection: (id) => api.post(`/directory/collections/${id}/share`),
  addToCollection: (profileId, data) => api.put(`/directory/favorites/${profileId}/collection`, data),
  viewSharedCollection: (token, password) => api.get(`/shared/collection/${token}`, { params: { password } }),
};

// Translation API (server returns source unchanged unless a cached/db
// translation exists — callers should fall back to the original text).
export const translateAPI = {
  translate: (text, from, to) => api.post('/translate', { text, from, to }),
  batch: (texts, from, to) => api.post('/translate/batch', { texts, from, to }),
};

// Matches API
export const matchesAPI = {
  getAll: (params) => api.get('/matches', { params }),
  getById: (id) => api.get(`/matches/${id}`),
  create: (data) => api.post('/matches', data),
  update: (id, data) => api.put(`/matches/${id}`, data),
  delete: (id) => api.delete(`/matches/${id}`),
  compare: (id) => api.get(`/matches/${id}/compare`),
  getPotential: (profileId, params) => api.get(`/matches/potential/${profileId}`, { params }),
};

// Messages API
export const messagesAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getConversation: (id) => api.get(`/messages/conversations/${id}`),
  getMessages: (conversationId) => api.get(`/messages/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, content) => 
    api.post(`/messages/conversations/${conversationId}`, { content }),
  createConversation: (participantIds) => 
    api.post('/messages/conversations', { participant_ids: participantIds }),
  getMatchmakers: () => api.get('/messages/matchmakers'),
  getUnreadCount: () => api.get('/messages/unread'),
  markAsRead: (conversationId) => api.put(`/messages/conversations/${conversationId}/read`),
};

// Attach to api object for legacy support
api.messages = {
  getConversations: () => api.get('/messages/conversations'),
  getConversation: (id) => api.get(`/messages/conversations/${id}`),
  getMessages: (conversationId) => api.get(`/messages/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, data) => 
    api.post(`/messages/conversations/${conversationId}`, typeof data === 'string' ? { content: data } : data),
  createConversation: (participantIds) => 
    api.post('/messages/conversations', { participant_ids: participantIds }),
  markAsRead: (conversationId) => api.put(`/messages/conversations/${conversationId}/read`),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  complete: (id) => api.post(`/tasks/${id}/complete`),
};

// Direct API methods for legacy support
api.getTasks = (params) => api.get('/tasks', { params });
api.getTask = (id) => api.get(`/tasks/${id}`);
api.createTask = (data) => api.post('/tasks', data);
api.updateTask = (id, data) => api.put(`/tasks/${id}`, data);
api.deleteTask = (id) => api.delete(`/tasks/${id}`);
api.completeTask = (id) => api.post(`/tasks/${id}/complete`);

// Calendar API
export const calendarAPI = {
  getEvents: (params) => api.get('/calendar', { params }),
  getEvent: (id) => api.get(`/calendar/${id}`),
  createEvent: (data) => api.post('/calendar', data),
  updateEvent: (id, data) => api.put(`/calendar/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/${id}`),
};

// Direct API methods for legacy support
api.getCalendarEvents = (params) => api.get('/calendar', { params });
api.createCalendarEvent = (data) => api.post('/calendar', data);
api.updateCalendarEvent = (id, data) => api.put(`/calendar/${id}`, data);
api.deleteCalendarEvent = (id) => api.delete(`/calendar/${id}`);

// Affiliate API
export const affiliateAPI = {
  getAccount: () => api.get('/affiliate/account'),
  getCommissions: () => api.get('/affiliate/commissions'),
  getPoints: () => api.get('/affiliate/points'),
  getTiers: () => api.get('/affiliate/tiers'),
  getDownline: () => api.get('/affiliate/downline'),
  getTeam: () => api.get('/affiliate/team'),
  requestWithdrawal: (data) => api.post('/affiliate/withdraw', data),
  getPayoutSettings: () => api.get('/affiliate/payout-settings'),
  getPayouts: () => api.get('/affiliate/payouts'),
  requestPayout: (data) => api.post('/affiliate/payouts', data),
  getPaymentInfo: () => api.get('/affiliate/payment-info'),
  savePaymentInfo: (data) => api.post('/affiliate/payment-info', data),
  uploadW9: (formData, options = {}) => api.post('/affiliate/w9', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...options
  }),
  checkPayoutEligibility: () => api.get('/affiliate/payout-eligibility'),
  // 1099 Tax Forms
  get1099Forms: () => api.get('/affiliate/1099-forms'),
  // Team Rankings
  getTeamRankings: () => api.get('/affiliate/team-rankings'),
};

// Billing API
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  getPublicPlans: () => api.get('/public/subscription-plans'),
  getPublicPaymentInstructions: () => api.get('/public/payment-instructions'),
  getSubscription: () => api.get('/billing/subscription'),
  subscribe: (planId) => api.post('/billing/subscription', { planId }),
  cancelSubscription: (id) => api.delete(`/billing/subscription/${id}`),
  getInvoices: (params) => api.get('/billing/invoices', { params }),
  getInvoice: (id) => api.get(`/billing/invoices/${id}`),
  createInvoice: (data) => api.post('/billing/invoices', data),
  processPayment: (data) => api.post('/billing/payment', data),
  getPaymentHistory: (params) => api.get('/billing/payments', { params }),
  
  // Payment Methods
  getPaymentMethods: () => api.get('/billing/payment-methods'),
  addPaymentMethod: (data) => api.post('/billing/payment-methods', data),
  updatePaymentMethod: (id, data) => api.put(`/billing/payment-methods/${id}`, data),
  deletePaymentMethod: (id) => api.delete(`/billing/payment-methods/${id}`),
  setDefaultPaymentMethod: (id) => api.put(`/billing/payment-methods/${id}/default`),
  
  // Membership invoices for client profiles
  createMembershipInvoice: (data) => api.post('/billing/membership-invoice', data),
  markInvoicePaid: (id, data) => api.put(`/billing/invoices/${id}/paid`, data),
  recordPayment: (id, data) => api.put(`/billing/invoices/${id}/record-payment`, data),
  uploadScreenshot: (id, data) => api.put(`/billing/invoices/${id}/screenshot`, data),
  // Commission/points + interviewer split toggle — coach/leader_coach can use
  // this for their own clients' invoices (ownership-gated in controller).
  updateInvoiceCommissionSettings: (id, data) => api.put(`/billing/invoices/${id}/commission-settings`, data),
  getClientInvoices: (params) => api.get('/billing/client-invoices', { params }),
  getProfileInvoices: (profileId, params) => api.get(`/billing/profile/${profileId}/invoices`, { params }),
  
  // Client subscriptions
  createClientSubscription: (data) => api.post('/billing/client-subscription', data),
  getClientSubscriptions: (params) => api.get('/billing/client-subscriptions', { params }),
  
  // Get membership plans from admin settings
  getMembershipPlans: () => api.get('/billing/membership-plans'),
  
  // Get payment instructions from platform settings
  getPaymentInstructions: () => api.get('/billing/payment-instructions'),
  
  // Client Billing (profile payments/invoices)
  getClientBilling: (params) => api.get('/billing/client-billing', { params }),
  getClientBillingById: (id) => api.get(`/billing/client-billing/${id}`),
  verifyPayment: (id, data) => api.post(`/billing/client-billing/${id}/verify`, data),
  rejectPayment: (id, data) => api.post(`/billing/client-billing/${id}/reject`, data),
  uploadPaymentProof: (id, formData) => api.post(`/billing/client-billing/${id}/upload-proof`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadItemProof: (itemId, data) => api.put(`/billing/items/${itemId}/proof`, data),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  // AI portrait video (MiniMax image-to-video → S3) for one member.
  aiVideoStart: (profileId) => api.post(`/admin/profiles/${profileId}/ai-video`),
  aiVideoStatus: (profileId) => api.get(`/admin/profiles/${profileId}/ai-video`),
  aiVideoClear: (profileId) => api.delete(`/admin/profiles/${profileId}/ai-video`),
  // Reuses the supervisor presigned-URL endpoint (route allows admin too).
  // Used by AdminKPI.js to open recording links — direct S3 URLs 403 because
  // the bucket is private.
  getVideoRecordingUrl: (recordingUrl) => api.get('/supervisor/kpi/video-recording-url', { params: { recordingUrl } }),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  generateLoginForUser: (id) => api.post(`/admin/users/${id}/generate-login`),
  testPushNotification: (id, body) => api.post(`/admin/users/${id}/test-push`, { body }),
  getRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRole: (id, data) => api.put(`/admin/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`),
  getPermissions: () => api.get('/admin/permissions'),
  getLogs: (params) => api.get('/admin/activity-logs', { params }),
  exportAllLogs: () => api.get('/admin/activity-logs/export-all'),
  clearLogs: () => api.delete('/admin/activity-logs'),
  getAllProfiles: (params) => api.get('/admin/profiles', { params }),
  getPendingProfiles: () => api.get('/admin/profiles/pending'),
  approveProfile: (id) => api.put(`/admin/profiles/${id}/approve`),
  rejectProfile: (id, data) => api.put(`/admin/profiles/${id}/reject`, data),
  bulkUpdateProfiles: (data) => api.put('/admin/profiles/bulk-update', data),
  updateProfile: (id, data) => api.put(`/admin/profiles/${id}`, data),

  // Affiliate Management
  createAffiliate: (data) => api.post('/admin/affiliates', data),
  getAffiliates: (params) => api.get('/admin/affiliates', { params }),
  getAffiliate: (id) => api.get(`/admin/affiliates/${id}`),
  updateAffiliate: (id, data) => api.put(`/admin/affiliates/${id}`, data),
  addAffiliatePoints: (id, data) => api.post(`/admin/affiliates/${id}/points`, data),
  getAffiliateTeam: (id) => api.get(`/admin/affiliates/${id}/team`),
  autoUpgradeAffiliates: () => api.post('/admin/affiliates/auto-upgrade'),
  getTeamRankings: () => api.get('/admin/affiliates/team-rankings'),
  
  // Commission Management
  getCommissions: (params) => api.get('/admin/commissions', { params }),
  createCommission: (data) => api.post('/admin/commissions', data),
  updateCommission: (id, data) => api.put(`/admin/commissions/${id}`, data),
  deleteCommission: (id) => api.delete(`/admin/commissions/${id}`),
  
  // Payout Management
  getPayoutSettings: () => api.get('/admin/payout-settings'),
  updatePayoutSettings: (data) => api.put('/admin/payout-settings', data),
  getPayouts: (params) => api.get('/admin/payouts', { params }),
  getPayout: (id) => api.get(`/admin/payouts/${id}`),
  createPayout: (data) => api.post('/admin/payouts', data),
  updatePayout: (id, data) => api.put(`/admin/payouts/${id}`, data),
  deletePayout: (id) => api.delete(`/admin/payouts/${id}`),
  
  // Affiliate Tiers
  getAffiliateTiers: () => api.get('/admin/affiliate-tiers'),
  createAffiliateTier: (data) => api.post('/admin/affiliate-tiers', data),
  updateAffiliateTier: (id, data) => api.put(`/admin/affiliate-tiers/${id}`, data),
  deleteAffiliateTier: (id) => api.delete(`/admin/affiliate-tiers/${id}`),
  
  // Institutional Partner Bonus Settings
  getInstitutionalBonusSettings: () => api.get('/admin/affiliate-settings/institutional-bonus'),
  saveInstitutionalBonusSettings: (data) => api.put('/admin/affiliate-settings/institutional-bonus', data),

  // Level Difference Bonus Settings (级差奖金)
  getLevelDiffBonusSettings: () => api.get('/admin/affiliate-settings/level-diff-bonus'),
  saveLevelDiffBonusSettings: (data) => api.put('/admin/affiliate-settings/level-diff-bonus', data),

  // Service Fee Settings (ProfileForm processing fee)
  getServiceFeeSettings: () => api.get('/admin/affiliate-settings/service-fee'),
  updateServiceFeeSettings: (data) => api.put('/admin/affiliate-settings/service-fee', data),

  // Admin W9 Review
  getPendingW9s: () => api.get('/admin/w9-pending'),
  getAllW9s: (params) => api.get('/admin/w9-all', { params }),
  downloadW9: (affiliateId) => api.get(`/admin/w9-download/${affiliateId}`, { responseType: 'blob' }),
  approveW9: (affiliateId) => api.put(`/admin/affiliates/${affiliateId}/w9/approve`),
  unapproveW9: (affiliateId) => api.put(`/admin/affiliates/${affiliateId}/w9/unapprove`),
  // Admin uploads a W-9 on behalf of an affiliate (out-of-band paperwork).
  uploadW9OnBehalf: (affiliateId, formData, options = {}) =>
    api.post(`/admin/affiliates/${affiliateId}/w9`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...options,
    }),
  rejectW9: (affiliateId, data) => api.put(`/admin/affiliates/${affiliateId}/w9/reject`, data),

  // Partner Agreements
  getPartnerAgreements: (params) => api.get('/admin/affiliates/partner-agreements', { params }),
  createPartnerAgreement: (data) => api.post('/admin/affiliates/partner-agreements', data),
  revokePartnerAgreement: (id) => api.put(`/admin/affiliates/partner-agreements/${id}/revoke`),

  // 联盟成员审核 — internal matchmakers awaiting admin approval
  getMemberReview: (params) => api.get('/admin/affiliates/member-review', { params }),
  reviewMember: (id, action) => api.put(`/admin/affiliates/member-review/${id}`, { action }),

  // Affiliate 1099 Tax Forms
  getAffiliate1099Forms: (affiliateId) => api.get(`/admin/affiliates/${affiliateId}/1099-forms`),
  uploadAffiliate1099: (formData) => api.post('/admin/affiliates/1099-forms', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAffiliate1099: (formId) => api.delete(`/admin/affiliates/1099-forms/${formId}`),
  
  // Plans
  getPlans: () => api.get('/admin/plans'),
  createPlan: (data) => api.post('/admin/plans', data),
  updatePlan: (id, data) => api.put(`/admin/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/admin/plans/${id}`),
  
  // Dating Schedules Management
  getDatingSchedules: (params) => api.get('/admin/dating-schedules', { params }),
  getDatingSchedule: (id) => api.get(`/admin/dating-schedules/${id}`),
  createDatingSchedule: (data) => api.post('/admin/dating-schedules', data),
  updateDatingSchedule: (id, data) => api.put(`/admin/dating-schedules/${id}`, data),
  updateDatingScheduleStatus: (id, data) => api.put(`/admin/dating-schedules/${id}/status`, data),
  deleteDatingSchedule: (id) => api.delete(`/admin/dating-schedules/${id}`),
  
  // Matches Management
  getMatches: (params) => api.get('/admin/matches', { params }),
  getMatch: (id) => api.get(`/admin/matches/${id}`),
  updateMatch: (id, data) => api.put(`/admin/matches/${id}`, data),
  deleteMatch: (id) => api.delete(`/admin/matches/${id}`),
  getMatchStats: () => api.get('/admin/matches/stats'),
  bulkUpdateMatches: (data) => api.put('/admin/matches/bulk-update', data),
  bulkDeleteMatches: (data) => api.delete('/admin/matches/bulk-delete', { data }),

  // Billing Management
  getAllInvoices: (params) => api.get('/admin/invoices', { params }),
  getInvoice: (id) => api.get(`/admin/invoices/${id}`),
  createInvoice: (data) => api.post('/admin/invoices', data),
  updateInvoice: (id, data) => api.put(`/admin/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/admin/invoices/${id}`),
  markInvoicePaid: (id, data) => api.put(`/admin/invoices/${id}/paid`, data),
  changeInvoiceStatus: (id, data) => api.put(`/admin/invoices/${id}/change-status`, data),
  uploadItemProof: (itemId, data) => api.put(`/admin/billing/items/${itemId}/proof`, data),
  updateItemDueDate: (itemId, data) => api.put(`/admin/billing/items/${itemId}/due-date`, data),

  // Client Membership Billing
  getClientMemberships: (params) => api.get('/admin/client-memberships', { params }),
  createClientMembership: (data) => api.post('/admin/client-memberships', data),
  createClientMembershipWithFile: (formData) => api.post('/admin/client-memberships', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateClientMembership: (id, data) => api.put(`/admin/client-memberships/${id}`, data),
  updateInvoiceWithFile: (id, formData) => api.put(`/admin/client-memberships/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteClientMembership: (id) => api.delete(`/admin/client-memberships/${id}`),
  approveClientMembership: (id) => api.put(`/admin/client-memberships/${id}/approve`),
  rejectClientMembership: (id, data) => api.put(`/admin/client-memberships/${id}/reject`, data),
  bulkUpdateClientMemberships: (data) => api.put('/admin/client-memberships-bulk', data),
  bulkPayments: (data) => api.post('/admin/billing/bulk-payments', data),
  getSubscriptionAccounts: (paymentMethod) => api.get('/admin/settings/subscription-accounts', { params: { method: paymentMethod } }),

  // Matchmaker Membership Billing
  getMatchmakerMemberships: (params) => api.get('/admin/matchmaker-memberships', { params }),
  createMatchmakerMembership: (data) => api.post('/admin/matchmaker-memberships', data),
  updateMatchmakerMembership: (id, data) => api.put(`/admin/matchmaker-memberships/${id}`, data),
  deleteMatchmakerMembership: (id) => api.delete(`/admin/matchmaker-memberships/${id}`),
  approveMatchmakerMembership: (id) => api.put(`/admin/matchmaker-memberships/${id}/approve`),
  rejectMatchmakerMembership: (id, data) => api.put(`/admin/matchmaker-memberships/${id}/reject`, data),

  // Platform Settings - Payment Instructions
  getPaymentInstructions: () => api.get('/admin/settings/payment-instructions'),
  updatePaymentInstructions: (data) => api.put('/admin/settings/payment-instructions', data),
  
  // Platform Settings - PayPal Integration
  getPaypalSettings: () => api.get('/admin/settings/paypal'),
  updatePaypalSettings: (data) => api.put('/admin/settings/paypal', data),
  testPaypalConnection: () => api.post('/admin/settings/paypal/test'),

  // Platform Settings - Payment Accounts (PayPal/Square)
  getPaymentAccounts: () => api.get('/admin/settings/payment-accounts'),
  savePaymentAccount: (data) => api.post('/admin/settings/payment-accounts', data),
  deletePaymentAccount: (type, accountId) => api.delete(`/admin/settings/payment-accounts/${type}/${accountId}`),

  // Platform Settings - Client Membership Plans
  getClientPlans: () => api.get('/admin/settings/client-plans'),
  getClientPlan: (id) => api.get(`/admin/settings/client-plans/${id}`),
  createClientPlan: (data) => api.post('/admin/settings/client-plans', data),
  updateClientPlan: (id, data) => api.put(`/admin/settings/client-plans/${id}`, data),
  deleteClientPlan: (id) => api.delete(`/admin/settings/client-plans/${id}`),
  toggleClientPlanStatus: (id) => api.put(`/admin/settings/client-plans/${id}/toggle-status`),
  
  // Platform Settings - Twilio SMS
  getTwilioSettings: () => api.get('/admin/settings/twilio'),
  updateTwilioSettings: (data) => api.put('/admin/settings/twilio', data),
  testTwilioConnection: () => api.post('/admin/settings/twilio/test'),
  
  // Platform Settings - Email/SMTP
  getEmailSettings: () => api.get('/admin/settings/email'),
  updateEmailSettings: (data) => api.put('/admin/settings/email', data),
  testEmailConnection: (data) => api.post('/admin/settings/email/test', data),
  
  // Platform Settings - Email Templates
  getEmailTemplates: () => api.get('/admin/settings/email-templates'),
  updateEmailTemplates: (data) => api.put('/admin/settings/email-templates', data),
  testEmailTemplate: (data) => api.post('/admin/settings/email-templates/test', data),
  
  // Trends Management
  getTrendCategories: () => api.get('/admin/trends/categories'),
  createTrendCategory: (data) => api.post('/admin/trends/categories', data),
  updateTrendCategory: (id, data) => api.put(`/admin/trends/categories/${id}`, data),
  deleteTrendCategory: (id) => api.delete(`/admin/trends/categories/${id}`),
  
  getTrends: (params) => api.get('/admin/trends', { params }),
  getTrend: (id) => api.get(`/admin/trends/${id}`),
  createTrend: (data) => api.post('/admin/trends', data),
  updateTrend: (id, data) => api.put(`/admin/trends/${id}`, data),
  deleteTrend: (id) => api.delete(`/admin/trends/${id}`),
  approveTrend: (id) => api.put(`/admin/trends/${id}/approve`),
  rejectTrend: (id) => api.put(`/admin/trends/${id}/reject`),
  pinTrend: (id) => api.put(`/admin/trends/${id}/pin`),

  // Comment moderation
  getTrendCommentsAdmin: (id) => api.get(`/admin/trends/${id}/comments`),
  approveTrendComment: (commentId) => api.put(`/admin/trends/comments/${commentId}/approve`),
  rejectTrendComment: (commentId) => api.put(`/admin/trends/comments/${commentId}/reject`),
  deleteTrendComment: (commentId) => api.delete(`/admin/trends/comments/${commentId}`),
  
  getTrendSettings: () => api.get('/admin/trends/settings'),
  updateTrendSettings: (data) => api.put('/admin/trends/settings', data),

  // Signed Documents / Contracts
  getContracts: (params) => api.get('/admin/contracts', { params }),
  clearContract: (profileId) => api.delete(`/admin/contracts/${profileId}`),
  saveContractAddendum: (profileId, addendum) => api.put(`/admin/contracts/${profileId}/addendum`, { addendum }),
  updateContractSignDate: (profileId, signDate) => api.put(`/admin/contracts/${profileId}/sign-date`, { signDate }),
  sendContract: (profileId, language) => api.post(`/admin/contracts/${profileId}/send`, { language }),

  // KPI Performance Management (legacy tasks)
  getKpiTasks: (params) => api.get('/admin/kpi/tasks', { params }),
  getKpiStats: (params) => api.get('/admin/kpi/stats', { params }),
  createKpiTask: (data) => api.post('/admin/kpi/tasks', data),
  updateKpiTask: (id, data) => api.put(`/admin/kpi/tasks/${id}`, data),
  deleteKpiTask: (id) => api.delete(`/admin/kpi/tasks/${id}`),
  getKpiStaff: () => api.get('/admin/kpi/staff'),

  // KPI Workflows
  getKpiWorkflows: () => api.get('/admin/kpi/workflows'),
  getKpiWorkflow: (id) => api.get(`/admin/kpi/workflows/${id}`),
  createKpiWorkflow: (data) => api.post('/admin/kpi/workflows', data),
  updateKpiWorkflow: (id, data) => api.put(`/admin/kpi/workflows/${id}`, data),
  reorderKpiWorkflows: (order) => api.put('/admin/kpi/workflows/reorder', { order }),
  deleteKpiWorkflow: (id) => api.delete(`/admin/kpi/workflows/${id}`),

  // KPI Client Assignments
  getKpiAssignments: (params) => api.get('/admin/kpi/assignments', { params }),
  getKpiAssignment: (id) => api.get(`/admin/kpi/assignments/${id}`),
  createKpiAssignment: (data) => api.post('/admin/kpi/assignments', data),
  updateKpiAssignment: (id, data) => api.put(`/admin/kpi/assignments/${id}`, data),
  deleteKpiAssignment: (id) => api.delete(`/admin/kpi/assignments/${id}`),

  // KPI Stage & Deliverable Completions
  updateKpiStageCompletion: (id, data) => api.put(`/admin/kpi/stage-completions/${id}`, data),
  updateKpiDeliverable: (id, data) => api.put(`/admin/kpi/deliverable-completions/${id}`, data),
  uploadKpiDeliverablePhotos: (id, formData) => api.post(`/admin/kpi/deliverable-completions/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // KPI Questionnaires
  getKpiQuestionnaires: () => api.get('/admin/kpi/questionnaires'),
  getKpiQuestionnaire: (id) => api.get(`/admin/kpi/questionnaires/${id}`),
  createKpiQuestionnaire: (data) => api.post('/admin/kpi/questionnaires', data),
  updateKpiQuestionnaire: (id, data) => api.put(`/admin/kpi/questionnaires/${id}`, data),
  deleteKpiQuestionnaire: (id) => api.delete(`/admin/kpi/questionnaires/${id}`),
  sendKpiQuestionnaire: (id, data) => api.post(`/admin/kpi/questionnaires/${id}/send`, data),
  getKpiQuestionnaireSubmissions: (params) => api.get('/admin/kpi/questionnaire-submissions', { params }),
  getKpiQuestionnaireResults: (questionnaireId, assignmentId) => api.get(`/kpi/questionnaire-results/${questionnaireId}/${assignmentId}`),

  // KPI Reminders
  getKpiReminders: (params) => api.get('/admin/kpi/reminders', { params }),
  createKpiReminder: (data) => api.post('/admin/kpi/reminders', data),
  updateKpiReminder: (id, data) => api.put(`/admin/kpi/reminders/${id}`, data),
  deleteKpiReminder: (id) => api.delete(`/admin/kpi/reminders/${id}`),

  // KPI Follow-ups (admin view all)
  getKpiFollowups: (params) => api.get('/admin/kpi/followups', { params }),
  deleteKpiFollowup: (id) => api.delete(`/admin/kpi/followups/${id}`),

  // KPI Confirmations (admin view all)
  getKpiConfirmations: (params) => api.get('/admin/kpi/confirmations', { params }),
  deleteKpiConfirmation: (id) => api.delete(`/admin/kpi/confirmations/${id}`),

  // Stage pages — admin uses the supervisor read endpoint (admin role is
  // already allowed there). Used to render the same paginated stage view
  // coaches see for stages with allow_page_creation = true.
  listKpiStagePages: (stageCompletionId) => api.get(`/supervisor/kpi/stages/${stageCompletionId}/pages`),

  // KPI Warnings
  getKpiWarnings: (params) => api.get('/admin/kpi/warnings', { params }),
  resolveKpiWarning: (id, data) => api.put(`/admin/kpi/warnings/${id}/resolve`, data),

  // KPI Dashboard Stats
  getKpiDashboardStats: () => api.get('/admin/kpi/dashboard-stats'),

  // KPI Summary (lightweight counts for dashboard & notifications)
  getKpiSummary: () => api.get('/admin/kpi/summary'),
  updateCoachPerformance: (userId, data) => api.put(`/admin/kpi/coach-performance/${userId}`, data),

  // KPI Approved Payments (for base amount)
  getKpiApprovedPayments: (params) => api.get('/admin/kpi/approved-payments', { params }),

  // KPI Weekly Contacts (admin view)
  getKpiWeeklyContacts: (params) => api.get('/admin/kpi/weekly-contacts', { params }),
  updateKpiClientPriority: (assignmentId, data) => api.put(`/admin/kpi/client-priority/${assignmentId}`, data),

  // Platform Settings - Video Meeting
  // Agora RTC Settings (legacy)
  getAgoraSettings: () => api.get('/admin/settings/agora-rtc'),
  updateAgoraSettings: (data) => api.put('/admin/settings/agora-rtc', data),
  // Tencent RTC (TRTC) Settings
  getTencentRtcSettings: () => api.get('/admin/settings/tencent-rtc'),
  updateTencentRtcSettings: (data) => api.put('/admin/settings/tencent-rtc', data),

  // AWS S3 Settings
  getAwsS3Settings: () => api.get('/admin/settings/aws-s3'),
  updateAwsS3Settings: (data) => api.put('/admin/settings/aws-s3', data),

  // Finance
  getFinanceSummary: (params) => api.get('/admin/finance/summary', { params }),
  getFinanceTransactions: (params) => api.get('/admin/finance/transactions', { params }),
  createFinanceTransaction: (data) => api.post('/admin/finance/transactions', data),
  updateFinanceTransaction: (id, data) => api.put(`/admin/finance/transactions/${id}`, data),
  deleteFinanceTransaction: (id) => api.delete(`/admin/finance/transactions/${id}`),
  getFinanceChart: (params) => api.get('/admin/finance/chart', { params }),
  getFinanceStatement: (params) => api.get('/admin/finance/statement', { params }),
  getFinanceOutstanding: (params) => api.get('/admin/finance/outstanding', { params }),

  // Automation Tasks
  getAutomationTasks: (params) => api.get('/admin/automation-tasks', { params }),
  getAutomationTask: (id) => api.get(`/admin/automation-tasks/${id}`),
  createAutomationTask: (data) => api.post('/admin/automation-tasks', data),
  updateAutomationTask: (id, data) => api.put(`/admin/automation-tasks/${id}`, data),
  deleteAutomationTask: (id) => api.delete(`/admin/automation-tasks/${id}`),
  toggleAutomationTask: (id) => api.put(`/admin/automation-tasks/${id}/toggle`),
  testAutomationTask: (id) => api.post(`/admin/automation-tasks/${id}/test`),
  getAutomationTaskLogs: (id, params) => api.get(`/admin/automation-tasks/${id}/logs`, { params }),
  getAvailableTaskTypes: () => api.get('/admin/automation-task-types'),
  getAvailableWebhooks: () => api.get('/admin/automation-webhooks'),

  // Automation Workflows (Phase 1 — Zapier-style graph editor)
  getWorkflowNodeTypes: () => api.get('/admin/automation/node-types'),
  listWorkflows: () => api.get('/admin/automation/workflows'),
  createWorkflow: (data) => api.post('/admin/automation/workflows', data),
  getWorkflow: (id) => api.get(`/admin/automation/workflows/${id}`),
  updateWorkflow: (id, data) => api.put(`/admin/automation/workflows/${id}`, data),
  deleteWorkflow: (id) => api.delete(`/admin/automation/workflows/${id}`),
  saveWorkflowGraph: (id, data) => api.put(`/admin/automation/workflows/${id}/graph`, data),
  listWorkflowRuns: (id) => api.get(`/admin/automation/workflows/${id}/runs`),

  // Starsign Settings
  getStarsignSettings: () => api.get('/admin/settings/starsign'),
  updateStarsignSettings: (data) => api.put('/admin/settings/starsign', data),

  // Star-sign (命盘) report submissions — review + resend.
  getStarsignReports: (params) => api.get('/admin/starsign-reports', { params }),
  resendStarsignReport: (id, regenerate = false) =>
    api.post(`/admin/starsign-reports/${id}/resend`, { regenerate }),

  // API Key Management
  getApiKeys: () => api.get('/admin/api-keys'),
  createApiKey: (data) => api.post('/admin/api-keys', data),
  updateApiKey: (id, data) => api.put(`/admin/api-keys/${id}`, data),
  deleteApiKey: (id) => api.delete(`/admin/api-keys/${id}`),
};

// Coach KPI API
export const coachKpiAPI = {
  // My Clients (all assigned profiles + KPI data)
  getMyClients: (params) => api.get('/coach/kpi/my-clients', { params }),
  // 客户跟进提醒 — 逾期联系 / 本周要联系 / 今明约见 / 多久未联系.
  getFollowupAlerts: (params) => api.get('/coach/followup-alerts', { params }),
  // 教练工作台总览 stats.
  getOverviewStats: (params) => api.get('/coach/overview-stats', { params }),
  updateClientStatus: (profileId, data) => api.put(`/coach/kpi/client-status/${profileId}`, data),
  updateClientPriority: (assignmentId, data) => api.put(`/coach/kpi/client-priority/${assignmentId}`, data),

  // Assignments
  getAssignments: (params) => api.get('/coach/kpi/assignments', { params }),
  getAssignment: (id) => api.get(`/coach/kpi/assignments/${id}`),
  getDashboardStats: () => api.get('/coach/kpi/dashboard-stats'),
  getIncompleteDeliverables: () => api.get('/coach/kpi/incomplete-deliverables'),

  // Deliverables
  submitDeliverable: (id, data) => api.put(`/coach/kpi/deliverable-completions/${id}`, data),
  uploadDeliverablePhoto: (id, formData) => api.post(`/coach/kpi/deliverable-completions/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDeliverablePhoto: (id, photoUrl) => api.delete(`/coach/kpi/deliverable-completions/${id}/photos`, {
    data: { photoUrl }
  }),
  requestStageCompletion: (id) => api.put(`/coach/kpi/stage-completions/${id}/request-complete`),

  // Questionnaires
  getQuestionnaires: () => api.get('/coach/kpi/questionnaires'),
  sendQuestionnaire: (id, data) => api.post(`/coach/kpi/questionnaires/${id}/send`, data),
  getSubmissions: (params) => api.get('/coach/kpi/questionnaire-submissions', { params }),
  getQuestionnaireResults: (questionnaireId, assignmentId) => api.get(`/kpi/questionnaire-results/${questionnaireId}/${assignmentId}`),

  // Confirmations
  getConfirmations: (params) => api.get('/coach/kpi/confirmations', { params }),
  createConfirmation: (data) => api.post('/coach/kpi/confirmations', data),
  updateConfirmation: (id, data) => api.put(`/coach/kpi/confirmations/${id}`, data),
  deleteConfirmation: (id) => api.delete(`/coach/kpi/confirmations/${id}`),

  // AI Generate
  generateConfirmationContent: (data) => api.post('/coach/kpi/ai-generate-confirmation', data),

  // Deliverable Candidates
  getDeliverableCandidates: (deliverableCompletionId) => api.get(`/coach/kpi/deliverable-candidates/${deliverableCompletionId}`),
  saveDeliverableCandidates: (data) => api.post('/coach/kpi/deliverable-candidates', data),
  searchProfiles: (params) => api.get('/coach/kpi/search-profiles', { params }),
  getP1ConfirmedProfiles: (assignmentId, currentDeliverableId) => api.get(`/coach/kpi/p1-confirmed-profiles/${assignmentId}`, { params: { current_deliverable_id: currentDeliverableId } }),

  // Reminders
  getReminders: (params) => api.get('/coach/kpi/reminders', { params }),
  createReminder: (data) => api.post('/coach/kpi/reminders', data),
  updateReminder: (id, data) => api.put(`/coach/kpi/reminders/${id}`, data),
  deleteReminder: (id) => api.delete(`/coach/kpi/reminders/${id}`),

  // Follow-ups
  getFollowups: (params) => api.get('/coach/kpi/followups', { params }),
  createFollowup: (data) => api.post('/coach/kpi/followups', data),
  updateFollowupOutcome: (id, data) => api.put(`/coach/kpi/followups/${id}/outcome`, data),
  updateFollowup: (id, data) => api.put(`/coach/kpi/followups/${id}`, data),
  deleteFollowup: (id) => api.delete(`/coach/kpi/followups/${id}`),

  // Video Meetings
  getMeetings: (params) => api.get('/coach/kpi/meetings', { params }),
  createMeeting: (data) => api.post('/coach/kpi/meetings', data),
  updateMeeting: (id, data) => api.put(`/coach/kpi/meetings/${id}`, data),
  deleteMeeting: (id) => api.delete(`/coach/kpi/meetings/${id}`),
  getMeetingToken: (id) => api.get(`/coach/kpi/meetings/${id}/token`),
  getMeetingByChannel: (channelName) => api.get(`/meeting/${channelName}`),
  generateMeetingToken: (channelName, uid) => api.post(`/meeting/${channelName}/token`, { uid }),
  updateMeetingStatus: (channelName, status) => api.put(`/meeting/${channelName}/status`, { status }),
  getRecordingStatus: (channelName) => api.get(`/meeting/${channelName}/recording-status`),

  // Match/Schedule Integration (assignment-scoped)
  createMatchForAssignment: (id, data) => api.post(`/coach/kpi/assignments/${id}/create-match`, data),
  createScheduleForAssignment: (id, data) => api.post(`/coach/kpi/assignments/${id}/create-schedule`, data),
  getAssignmentMatches: (id) => api.get(`/coach/kpi/assignments/${id}/matches`),
  getAssignmentSchedules: (id) => api.get(`/coach/kpi/assignments/${id}/schedules`),

  // Matches (coach-scoped)
  getMatches: (params) => api.get('/coach/kpi/matches', { params }),
  createMatch: (data) => api.post('/coach/kpi/matches', data),
  updateMatch: (id, data) => api.put(`/coach/kpi/matches/${id}`, data),

  // Match Reviews
  createMatchReview: (data) => api.post('/coach/kpi/match-reviews', data),
  getMatchReviews: (params) => api.get('/coach/kpi/match-reviews', { params }),

  // Upcoming Items (dashboard modal)
  getUpcomingItems: () => api.get('/coach/kpi/upcoming-items'),

  // Appointments
  getAppointments: () => api.get('/coach/kpi/appointments'),
  createAppointment: (data) => api.post('/coach/kpi/appointments', data),
  updateAppointment: (id, data) => api.put(`/coach/kpi/appointments/${id}`, data),
  deleteAppointment: (id) => api.delete(`/coach/kpi/appointments/${id}`),

  // Dating Schedules (coach-scoped)
  getSchedules: (params) => api.get('/coach/kpi/schedules', { params }),
  createSchedule: (data) => api.post('/coach/kpi/schedules', data),
  updateScheduleStatus: (id, data) => api.put(`/coach/kpi/schedules/${id}/status`, data),

  // Member Profiles (all active member-type profiles for matching)
  getMemberProfiles: (params) => api.get('/coach/kpi/member-profiles', { params }),

  // Workflows (for assignment creation)
  getWorkflows: () => api.get('/coach/kpi/workflows'),

  // Create Assignment (coach creates for own clients)
  createAssignment: (data) => api.post('/coach/kpi/assignments', data),

  // Client Payment Details
  getClientPayments: (profileId) => api.get(`/coach/kpi/client-payments/${profileId}`),

  // Stage Feedback (read-only)
  getStageFeedback: (id) => api.get(`/coach/kpi/stage-completions/${id}/feedback`),

  // Weekly Contact Tracking
  getWeeklyContacts: (params) => api.get('/coach/kpi/weekly-contacts', { params }),
  submitWeeklyContact: (data) => api.post('/coach/kpi/weekly-contacts', data),

  // Client Priorities
  getClientPriorities: (params) => api.get('/coach/kpi/client-priorities', { params }),

  // Auto Recommendations
  getAutoRecommendations: (params) => api.get('/coach/kpi/auto-recommendations', { params }),

  // Video Recording URL (presigned S3 URL)
  getVideoRecordingUrl: (recordingUrl) => api.get('/supervisor/kpi/video-recording-url', { params: { recordingUrl } }),

  // Stage Pages (for stages with allow_page_creation = true)
  createStagePage: (data) => api.post('/coach/kpi/stage-pages', data),
  listStagePages: (stageCompletionId) => api.get(`/coach/kpi/stages/${stageCompletionId}/pages`),
  updateStagePage: (id, data) => api.put(`/coach/kpi/stage-pages/${id}`, data),
  deleteStagePage: (id) => api.delete(`/coach/kpi/stage-pages/${id}`),

  // Stage Page Deliverables
  addPageDeliverable: (pageId, data) => api.post(`/coach/kpi/stage-pages/${pageId}/deliverables`, data),
  updatePageDeliverable: (id, data) => api.put(`/coach/kpi/stage-page-deliverables/${id}`, data),
  deletePageDeliverable: (id) => api.delete(`/coach/kpi/stage-page-deliverables/${id}`),

  // KPI Profile & Deductions
  updateKpiProfile: (assignmentId, kpi_profile) => api.put(`/coach/kpi/clients/${assignmentId}/kpi-profile`, { kpi_profile }),
  getClientKpiInfo: (assignmentId) => api.get(`/coach/kpi/clients/${assignmentId}/kpi-info`),
  getClientDeductions: (assignmentId) => api.get(`/coach/kpi/clients/${assignmentId}/deductions`),
  getCoachWarnings: (params) => api.get('/coach/kpi/warnings', { params }),
  markWarningAsRead: (warningId) => api.put(`/coach/kpi/warnings/${warningId}/read`),
};

// Supervisor KPI API
export const supervisorAPI = {
  getDashboardStats: () => api.get('/supervisor/kpi/dashboard-stats'),
  getAssignments: (params) => api.get('/supervisor/kpi/assignments', { params }),
  getAssignment: (id) => api.get(`/supervisor/kpi/assignments/${id}`),
  reviewStageCompletion: (id, data) => api.put(`/supervisor/kpi/stage-completions/${id}/review`, data),
  addFeedback: (id, data) => api.post(`/supervisor/kpi/stage-completions/${id}/feedback`, data),
  getFeedback: (id) => api.get(`/supervisor/kpi/stage-completions/${id}/feedback`),
  getWarnings: () => api.get('/supervisor/kpi/warnings'),
  getStaff: () => api.get('/supervisor/kpi/staff'),
  getPendingApprovals: () => api.get('/supervisor/kpi/pending-approvals'),
  getQuestionnaireResults: (questionnaireId, assignmentId) => api.get(`/kpi/questionnaire-results/${questionnaireId}/${assignmentId}`),
  approveDeliverable: (id, data) => api.put(`/supervisor/kpi/deliverable-completions/${id}/approve`, data),
  getVideoRecordingUrl: (recordingUrl) => api.get('/supervisor/kpi/video-recording-url', { params: { recordingUrl } }),
  listStagePages: (stageCompletionId) => api.get(`/supervisor/kpi/stages/${stageCompletionId}/pages`),

  // Leader Coach Review
  reviewStageAsLeaderCoach: (id, data) => api.put(`/supervisor/kpi/stage-completions/${id}/leader-review`, data),
};

// Leader Coach API
export const leaderCoachAPI = {
  getDashboardStats: () => api.get('/leader-coach/kpi/dashboard-stats'),
  getAssignments: (params) => api.get('/leader-coach/kpi/assignments', { params }),
  getAssignment: (id) => api.get(`/leader-coach/kpi/assignments/${id}`),
  reviewStageCompletion: (id, data) => api.put(`/supervisor/kpi/stage-completions/${id}/review`, data),
  addFeedback: (id, data) => api.post(`/supervisor/kpi/stage-completions/${id}/feedback`, data),
  getFeedback: (id) => api.get(`/supervisor/kpi/stage-completions/${id}/feedback`),
  getWarnings: () => api.get('/supervisor/kpi/warnings'),
  getStaff: () => api.get('/supervisor/kpi/staff'),
  getPendingApprovals: () => api.get('/leader-coach/kpi/pending-stages'),
  getQuestionnaireResults: (questionnaireId, assignmentId) => api.get(`/kpi/questionnaire-results/${questionnaireId}/${assignmentId}`),
  approveDeliverable: (id, data) => api.put(`/supervisor/kpi/deliverable-completions/${id}/approve`, data),
  getVideoRecordingUrl: (recordingUrl) => api.get('/supervisor/kpi/video-recording-url', { params: { recordingUrl } }),
  listStagePages: (stageCompletionId) => api.get(`/leader-coach/kpi/stages/${stageCompletionId}/pages`),
  approveStage: (id, data) => api.post(`/leader-coach/kpi/approve-stage/${id}`, data),
  rejectStage: (id, data) => api.post(`/leader-coach/kpi/reject-stage/${id}`, data),
};

// Trends API (for clients)
export const trendsAPI = {
  getAll: (params) => api.get('/trends', { params }),
  getById: (id) => api.get(`/trends/${id}`),
  create: (data) => api.post('/trends', data),
  like: (id) => api.post(`/trends/${id}/like`),
  unlike: (id) => api.delete(`/trends/${id}/like`),
  getComments: (id) => api.get(`/trends/${id}/comments`),
  addComment: (id, data) => api.post(`/trends/${id}/comments`, data),
  deleteComment: (trendId, commentId) => api.delete(`/trends/${trendId}/comments/${commentId}`),
  vote: (id, option) => api.post(`/trends/${id}/vote`, { option }),
};

// Dating Schedules API (for matchmakers)
export const datingScheduleAPI = {
  getAll: (params) => api.get('/dating-schedules', { params }),
  getById: (id) => api.get(`/dating-schedules/${id}`),
  create: (data) => api.post('/dating-schedules', data),
  update: (id, data) => api.put(`/dating-schedules/${id}`, data),
  updateStatus: (id, data) => api.put(`/dating-schedules/${id}/status`, data),
  submitFeedback: (id, data) => api.put(`/dating-schedules/${id}/feedback`, data),
  delete: (id) => api.delete(`/dating-schedules/${id}`),
  getStats: () => api.get('/dating-schedules/stats'),
  getMatchmakers: () => api.get('/dating-schedules/matchmakers'),
  getReminders: () => api.get('/dating-schedules/reminders'),
  markReminderSent: (id, data) => api.put(`/dating-schedules/${id}/reminder`, data),
};

// Connections API (for finding external matchmakers and companies)
export const connectionsAPI = {
  getMatchmakers: (params) => api.get('/connections/matchmakers', { params }),
  getCompanies: (params) => api.get('/connections/companies', { params }),
};

// Public Questionnaire API (no auth)
export const publicQuestionnaireAPI = {
  getQuestionnaire: (token) => api.get(`/public/questionnaire/${token}`),
  submitQuestionnaire: (token, data) => api.post(`/public/questionnaire/${token}/submit`, data),
  confirmQuestionnaire: (token) => api.post(`/public/questionnaire/${token}/confirm`),
};

// Public Match Review API (no auth)
export const publicMatchReviewAPI = {
  getMatchReview: (token) => api.get(`/public/match-review/${token}`),
  respondToMatchReview: (token, data) => api.post(`/public/match-review/${token}/confirm`, data),
};

// Public Confirmation API (no auth)
export const publicConfirmationAPI = {
  getConfirmation: (token) => api.get(`/public/confirmation/${token}`),
  respondToConfirmation: (token, data) => api.post(`/public/confirmation/${token}/respond`, data),
};

// Meeting recording feedback — comment thread visible to coach / leader_coach
// / supervisor_coach / admin / super_admin per meeting.
export const recordingFeedbackAPI = {
  list: (meetingId) => api.get('/meeting-recording-feedback', { params: { meeting_id: meetingId } }),
  create: ({ meeting_id, recording_url, content }) =>
    api.post('/meeting-recording-feedback', { meeting_id, recording_url, content }),
  remove: (id) => api.delete(`/meeting-recording-feedback/${id}`),
};

// Payroll — leader_coach drafts → supervisor approves → admin final-approves
// → coach sees their slip via myPayslips().
export const payrollAPI = {
  listPeriods: () => api.get('/payroll/periods'),
  createPeriod: (data) => api.post('/payroll/periods', data),
  getPeriod: (id) => api.get(`/payroll/periods/${id}`),
  transition: (id, transition, note) => api.put(`/payroll/periods/${id}/status`, { transition, note }),
  deletePeriod: (id) => api.delete(`/payroll/periods/${id}`),
  addItem: (periodId, data) => api.post(`/payroll/periods/${periodId}/items`, data),
  updateItem: (periodId, itemId, data) => api.put(`/payroll/periods/${periodId}/items/${itemId}`, data),
  deleteItem: (periodId, itemId) => api.delete(`/payroll/periods/${periodId}/items/${itemId}`),
  listCoaches: () => api.get('/payroll/coaches'),
  listClients: (q) => api.get('/payroll/clients', { params: q ? { q } : {} }),
  myPayslips: () => api.get('/payroll/my-payslips'),
};

// Booking Link — sharable public booking page powered by a per-user slug.
export const bookingLinkAPI = {
  getMine: () => api.get('/booking-link/me'),
  updateMine: (data) => api.put('/booking-link/me', data),
  regenerate: () => api.post('/booking-link/me/regenerate'),
  listBookings: () => api.get('/booking-link/me/bookings'),
  // Public — used by the /book/:slug page (no auth)
  getPublic: (slug) => api.get(`/booking-link/public/${slug}`),
  getSlots: (slug, date, duration) => api.get(`/booking-link/public/${slug}/slots`, { params: { date, duration } }),
  bookPublic: (slug, data) => api.post(`/booking-link/public/${slug}`, data),
};

// Video Meetings — general-purpose page at /meetings (separate from KPI flow,
// shared underlying kpi_video_meetings table).
export const meetingsAPI = {
  list: (params) => api.get('/meetings', { params }),
  importableSchedules: () => api.get('/meetings/importable-schedules'),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
  deleteRecording: (id, recordingUrl) =>
    api.delete(`/meetings/${id}/recordings`, { data: { recording_url: recordingUrl } }),
};

// Public star-sign (命盘) report form — no auth required. Submits multipart
// form-data (optional avatar file + birth-data fields); backend generates the
// report and emails it.
export const starsignReportAPI = {
  submit: (formData) => api.post('/public/starsign-report', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Proxy a photo through the backend cartoonify provider. Returns
  // { url } on success or { fallback: true } when not configured / failed.
  cartoonify: (formData) => api.post('/public/cartoonify', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // System-configured option lists (marital status / education / etc).
  getOptions: () => api.get('/public/starsign-options'),
  // AI-polish a self-introduction from rough notes.
  generateBio: (data) => api.post('/public/starsign-bio', data),
  // Optional follow-up step offered on the result screen: fill in birthplace &
  // co, then regenerate the report with the real 月亮 / 金星 sections.
  submitDetails: (id, data) => api.post(`/public/starsign-report/${id}/details`, data),
};

// Team system-issue / bug tracker (Help page).
export const issuesAPI = {
  list: (params) => api.get('/issues', { params }),
  create: (data) => api.post('/issues', data),
  updateStatus: (id, status) => api.patch(`/issues/${id}`, { status }),
  update: (id, data) => api.put(`/issues/${id}`, data),
  remove: (id) => api.delete(`/issues/${id}`),
};

// Global admin theme (stored server-side so every user/device follows it).
export const themeAPI = {
  getPublic: () => api.get('/public/theme'),
  save: (theme) => api.put('/admin/theme', { theme }),
};

// 活动与成长项目 — organiser side (staff who opened the 活动管理 / 成长项目管理 module).
export const organizerAPI = {
  getMe: () => api.get('/organizer/me'),
  apply: (data) => api.post('/organizer/applications', data), // { module, intro, plan }
  listExperiences: (params) => api.get('/organizer/experiences', { params }), // { type, status, all }
  getExperience: (id) => api.get(`/organizer/experiences/${id}`),
  // 录制播放: one lesson video → { video_key, video_name, video_size }. No timeout — large files.
  uploadProgramVideo: (file, { experienceId, onProgress } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (experienceId) fd.append('experience_id', experienceId);
    return api.post('/organizer/program-videos', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0,
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    });
  },
  sessionVideo: (id, sid) => api.get(`/organizer/experiences/${id}/sessions/${sid}/video`), // { url } (short-lived)
  createExperience: (data) => api.post('/organizer/experiences', data),
  updateExperience: (id, data) => api.put(`/organizer/experiences/${id}`, data),
  deleteExperience: (id) => api.delete(`/organizer/experiences/${id}`),
  submitExperience: (id) => api.post(`/organizer/experiences/${id}/submit`),
  cancelExperience: (id) => api.post(`/organizer/experiences/${id}/cancel`),
  // ---- phase 3: check-in, attendees, helpers, earnings ----
  checkinExperiences: () => api.get('/organizer/checkin-experiences'),
  // { experience_id, qr_token?, ticket_id?, session_id?, method: 'scan'|'manual', client_ts? } → always 200 with { result, … }
  checkin: (data) => api.post('/organizer/checkin', data, { timeout: 15000 }),
  undoCheckin: (data) => api.delete('/organizer/checkin', { data }), // { experience_id, ticket_id, session_id? }
  checkinStats: (id, params) => api.get(`/organizer/experiences/${id}/checkin-stats`, { params, timeout: 15000 }), // { session_id }
  attendees: (id) => api.get(`/organizer/experiences/${id}/attendees`),
  attendeesCsv: (id) => api.get(`/organizer/experiences/${id}/attendees.csv`, { responseType: 'blob' }),
  notify: (id, data) => api.post(`/organizer/experiences/${id}/notify`, data), // { title, message, email }
  helpers: (id) => api.get(`/organizer/experiences/${id}/helpers`),
  addHelper: (id, data) => api.post(`/organizer/experiences/${id}/helpers`, data), // { user_id } | { email }
  removeHelper: (id, userId) => api.delete(`/organizer/experiences/${id}/helpers`, { data: { user_id: userId } }),
  staffSearch: (q) => api.get('/organizer/staff-search', { params: { q } }),
  earnings: () => api.get('/organizer/earnings'),
  requestPayout: (data) => api.post('/organizer/payouts', data), // { amount, method, details }
  // Cover / sponsor logo upload; returns { url }.
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  },
};

// 活动与成长项目 — admin side (applications, content review, commission).
export const adminExperiencesAPI = {
  listApplications: (params) => api.get('/admin/organizer-applications', { params }), // { status }
  reviewApplication: (id, data) => api.put(`/admin/organizer-applications/${id}`, data), // { action, reject_reason }
  updateOrganizerModule: (data) => api.put('/admin/organizer-modules', data), // { user_id, module, enabled, commission_override, approvals_left }
  listExperiences: (params) => api.get('/admin/experiences', { params }), // { status, type }
  reviewExperience: (id, data) => api.put(`/admin/experiences/${id}/review`, data), // { action, note }
  getCommission: () => api.get('/admin/settings/experience-commission'),
  saveCommission: (data) => api.put('/admin/settings/experience-commission', data), // { event, program }
  // ---- phase 3: revenue, orders, organiser payouts ----
  listPayouts: (params) => api.get('/admin/organizer-payouts', { params }), // { status }
  processPayout: (id, data) => api.put(`/admin/organizer-payouts/${id}`, data), // { action: 'paid'|'reject', reference, note }
  revenue: (params) => api.get('/admin/experience-revenue', { params }), // { from, to } YYYY-MM-DD
  listOrders: (params) => api.get('/admin/experience-orders', { params }), // { experience_id, status, page, limit }
  refundOrder: (id, data) => api.post(`/admin/experience-orders/${id}/refund`, data), // { amount?, reason? }
};

export default api;
